import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { viewTutorDetail } from '../../slice/tutorSlice'
import moment from 'moment'
import { hasPermission } from '../../common/permissions'

const ViewTutorDetail = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    const tutorId = location.state?.tutorId
    const { tutorDetail, loading } = useSelector(state => state.tutor)
    const [activeTab, setActiveTab] = useState('personal')
    const [selectedImage, setSelectedImage] = useState(null)

    useEffect(() => {
        if (!tutorId) {
            navigate('/tutors')
            return
        }
        dispatch(viewTutorDetail({ id: tutorId }))
    }, [tutorId, dispatch, navigate])

    if (loading) {
        return (
            <div className="container-fluid px-4 py-3">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        )
    }

    if (!tutorDetail) {
        return (
            <div className="container-fluid px-4 py-3">
                <div className="alert alert-warning">Tutor not found</div>
            </div>
        )
    }

    const getStatusBadge = (status) => {
        const statusConfig = {
            'pending': { bg: 'bg-warning', text: 'Pending' },
            'approved': { bg: 'bg-success', text: 'Approved' },
            'rejected': { bg: 'bg-danger', text: 'Rejected' }
        }
        const config = statusConfig[status] || { bg: 'bg-secondary', text: status }
        return <span className={`badge ${config.bg}`}>{config.text}</span>
    }

    const getGenderBadge = (gender) => {
        return gender === 'male' 
            ? <span className="badge bg-primary">Male</span>
            : gender === 'female'
            ? <span className="badge bg-info">Female</span>
            : <span className="badge bg-secondary">Other</span>
    }

    const ImageModal = ({ image, onClose }) => {
        if (!image) return null
        return (
            <div 
                className="modal d-block" 
                style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
                onClick={onClose}
            >
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content bg-transparent border-0">
                        <div className="modal-body p-0 text-center">
                            <img 
                                src={image} 
                                alt="Preview" 
                                style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '8px' }}
                            />
                            <button 
                                className="btn btn-light mt-3"
                                onClick={onClose}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-3">
                    <button
                        onClick={() => navigate('/tutors')}
                        className="btn btn-outline-secondary"
                        style={{
                            width: '36px',
                            height: '36px',
                            padding: '0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '6px'
                        }}
                    >
                        <i className="fe-arrow-left" style={{ fontSize: '16px' }} />
                    </button>
                    <h4 className="mb-0 fw-semibold">Tutor Details</h4>
                </div>

                {hasPermission('tutors', 'edit') && (
                    <Link
                        to="/tutors/edit"
                        state={{ tutorId }}
                        className="btn btn-primary"
                        style={{
                            padding: '8px 20px',
                            fontSize: '14px',
                            fontWeight: '500',
                            borderRadius: '6px'
                        }}
                    >
                        <i className="fe-edit me-2" style={{ fontSize: '14px' }} />
                        Edit Tutor
                    </Link>
                )}
            </div>

            {/* Profile Overview Card */}
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px' }}>
                <div className="card-body p-4">
                    <div className="row align-items-center">
                        <div className="col-auto">
                            <img
                                src={tutorDetail.profile_picture || process.env.PUBLIC_URL + '/assets/avatar.png'}
                                alt="Profile"
                                onError={(e) => {
                                    e.currentTarget.onerror = null
                                    e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png'
                                }}
                                style={{
                                    width: '100px',
                                    height: '100px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '3px solid #dee2e6'
                                }}
                            />
                        </div>
                        <div className="col">
                            <h3 className="mb-2 fw-bold">
                                {`${tutorDetail.first_name || ''} ${tutorDetail.last_name || ''}`.trim() || 'N/A'}
                            </h3>
                            <p className="text-muted mb-2">
                                <i className="fe-mail me-2"></i>
                                {tutorDetail.email || 'N/A'}
                            </p>
                            <p className="text-muted mb-2">
                                <i className="fe-phone me-2"></i>
                                {tutorDetail.country_code && tutorDetail.phone_number 
                                    ? `${tutorDetail.country_code}${tutorDetail.phone_number}`
                                    : 'N/A'}
                            </p>
                            <div className="d-flex gap-2 flex-wrap">
                                {getStatusBadge(tutorDetail.profile_status)}
                                {getGenderBadge(tutorDetail.gender)}
                                <span className={`badge ${tutorDetail.is_active === 1 ? 'bg-success' : 'bg-danger'}`}>
                                    {tutorDetail.is_active === 1 ? 'Active' : 'Inactive'}
                                </span>
                                {/* {tutorDetail.is_verified === 1 && (
                                    <span className="badge bg-primary">
                                        <i className="fe-check-circle me-1"></i>Verified
                                    </span>
                                )} */}
                                {tutorDetail.is_premium_verified === 1 && (
                                    <span className="badge bg-warning text-dark">
                                        <i className="fe-star me-1"></i>Premium
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px' }}>
                <div className="card-body p-0">
                    <ul className="nav nav-tabs" style={{ borderBottom: '1px solid #dee2e6' }}>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'personal' ? 'active' : ''}`}
                                onClick={() => setActiveTab('personal')}
                                style={{
                                    border: 'none',
                                    borderBottom: activeTab === 'personal' ? '2px solid #0d6efd' : '2px solid transparent',
                                    color: activeTab === 'personal' ? '#0d6efd' : '#6c757d',
                                    fontWeight: '500',
                                    padding: '12px 24px'
                                }}
                            >
                                Personal Information
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'professional' ? 'active' : ''}`}
                                onClick={() => setActiveTab('professional')}
                                style={{
                                    border: 'none',
                                    borderBottom: activeTab === 'professional' ? '2px solid #0d6efd' : '2px solid transparent',
                                    color: activeTab === 'professional' ? '#0d6efd' : '#6c757d',
                                    fontWeight: '500',
                                    padding: '12px 24px'
                                }}
                            >
                                Professional Details
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'documents' ? 'active' : ''}`}
                                onClick={() => setActiveTab('documents')}
                                style={{
                                    border: 'none',
                                    borderBottom: activeTab === 'documents' ? '2px solid #0d6efd' : '2px solid transparent',
                                    color: activeTab === 'documents' ? '#0d6efd' : '#6c757d',
                                    fontWeight: '500',
                                    padding: '12px 24px'
                                }}
                            >
                                Documents
                            </button>
                        </li>
                    </ul>
                </div>
            </div>

            {/* Personal Information Tab */}
            {activeTab === 'personal' && (
                <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                    <div className="card-body p-4">
                        <h5 className="mb-4 fw-semibold">Personal Information</h5>
                        <div className="row g-4">
                            <div className="col-md-6">
                                <label className="text-muted small mb-1">User ID</label>
                                <p className="mb-0 fw-semibold">{tutorDetail.id || 'N/A'}</p>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Username</label>
                                <p className="mb-0 fw-semibold">{tutorDetail.username || 'N/A'}</p>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">First Name</label>
                                <p className="mb-0 fw-semibold">{tutorDetail.first_name || 'N/A'}</p>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Last Name</label>
                                <p className="mb-0 fw-semibold">{tutorDetail.last_name || 'N/A'}</p>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Email</label>
                                <p className="mb-0 fw-semibold">{tutorDetail.email || 'N/A'}</p>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Phone Number</label>
                                <p className="mb-0 fw-semibold">
                                    {tutorDetail.country_code && tutorDetail.phone_number 
                                        ? `${tutorDetail.country_code}${tutorDetail.phone_number}`
                                        : 'N/A'}
                                </p>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Date of Birth</label>
                                <p className="mb-0 fw-semibold">
                                    {tutorDetail.dob ? moment(tutorDetail.dob).format('DD MMM YYYY') : 'N/A'}
                                </p>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Gender</label>
                                <p className="mb-0">{getGenderBadge(tutorDetail.gender)}</p>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Emirates ID</label>
                                <p className="mb-0 fw-semibold">{tutorDetail.emirates_id || 'N/A'}</p>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Emirate/State</label>
                                <p className="mb-0 fw-semibold">
                                    {tutorDetail.emirate?.en_name || 'N/A'}
                                    {tutorDetail.emirate?.ar_name && (
                                        <span className="text-muted ms-2">({tutorDetail.emirate.ar_name})</span>
                                    )}
                                </p>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Language Preference</label>
                                <p className="mb-0 fw-semibold">
                                    {tutorDetail.lang === 'en' ? 'English' : tutorDetail.lang === 'ar' ? 'Arabic' : 'N/A'}
                                </p>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Languages Spoken</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {tutorDetail.languages_spoken && tutorDetail.languages_spoken.length > 0 ? (
                                        tutorDetail.languages_spoken.map((lang, index) => (
                                            <span key={index} className="badge bg-info">{lang}</span>
                                        ))
                                    ) : (
                                        <span className="text-muted">N/A</span>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-12">
                                <label className="text-muted small mb-1">Bio</label>
                                <p className="mb-0 fw-semibold" style={{ whiteSpace: 'pre-wrap' }}>
                                    {tutorDetail.bio || 'N/A'}
                                </p>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Created Date</label>
                                <p className="mb-0 fw-semibold">
                                    {tutorDetail.createdAt 
                                        ? moment(tutorDetail.createdAt).local().format('DD MMM YYYY, hh:mm A')
                                        : 'N/A'}
                                </p>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Last Updated</label>
                                <p className="mb-0 fw-semibold">
                                    {tutorDetail.updatedAt 
                                        ? moment(tutorDetail.updatedAt).local().format('DD MMM YYYY, hh:mm A')
                                        : 'N/A'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Professional Details Tab */}
            {activeTab === 'professional' && (
                <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                    <div className="card-body p-4">
                        <h5 className="mb-4 fw-semibold">Professional Details</h5>
                        <div className="row g-4">
                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Educational Qualification</label>
                                <p className="mb-0 fw-semibold">{tutorDetail.educational_qualification || 'N/A'}</p>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Subjects Taught</label>
                                <p className="mb-0 fw-semibold">{tutorDetail.subjects_taught || 'N/A'}</p>
                            </div>

                            <div className="col-md-12">
                                <label className="text-muted small mb-1">Subjects</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {tutorDetail.subjects && tutorDetail.subjects.length > 0 ? (
                                        tutorDetail.subjects.map((subject) => (
                                            <span key={subject.id} className="badge bg-primary" style={{ fontSize: '13px', padding: '6px 12px' }}>
                                                {subject.option_en}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-muted">N/A</span>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-12">
                                <label className="text-muted small mb-1">Curriculum</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {tutorDetail.curriculums && tutorDetail.curriculums.length > 0 ? (
                                        tutorDetail.curriculums.map((curr) => (
                                            <span key={curr.id} className="badge bg-success" style={{ fontSize: '13px', padding: '6px 12px' }}>
                                                {curr.option_en}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-muted">N/A</span>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-12">
                                <label className="text-muted small mb-1">Grades</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {tutorDetail.grade_details && tutorDetail.grade_details.length > 0 ? (
                                        tutorDetail.grade_details.map((grade) => (
                                            <span key={grade.id} className="badge bg-warning text-dark" style={{ fontSize: '13px', padding: '6px 12px' }}>
                                                {grade.option_en}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-muted">N/A</span>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-12">
                                <label className="text-muted small mb-1">Age Range</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {tutorDetail.age_range_details && tutorDetail.age_range_details.length > 0 ? (
                                        tutorDetail.age_range_details.map((age) => (
                                            <span key={age.id} className="badge bg-info" style={{ fontSize: '13px', padding: '6px 12px' }}>
                                                {age.name}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-muted">N/A</span>
                                    )}
                                </div>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Commission Percentage</label>
                                <p className="mb-0 fw-semibold">{tutorDetail.commission_percentage || 0}%</p>
                            </div>

                            <div className="col-md-6">
                                <label className="text-muted small mb-1">Profile Step</label>
                                <p className="mb-0 fw-semibold">{tutorDetail.step || 0}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
                <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                    <div className="card-body p-4">
                        <h5 className="mb-4 fw-semibold">Documents</h5>
                        
                        <div className="row g-4">
                            {/* Profile Picture */}
                            <div className="col-md-6">
                                <label className="text-muted small mb-2 d-block">Profile Picture</label>
                                {tutorDetail.profile_picture ? (
                                    <div 
                                        className="border rounded p-2 text-center"
                                        style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                                        onClick={() => setSelectedImage(tutorDetail.profile_picture)}
                                    >
                                        <img
                                            src={tutorDetail.profile_picture}
                                            alt="Profile"
                                            style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px' }}
                                        />
                                        <p className="small text-muted mt-2 mb-0">Click to enlarge</p>
                                    </div>
                                ) : (
                                    <p className="text-muted">Not uploaded</p>
                                )}
                            </div>

                            {/* Video Introduction */}
                            <div className="col-md-6">
                                <label className="text-muted small mb-2 d-block">Video Introduction</label>
                                {tutorDetail.video_introduction ? (
                                    <video 
                                        controls 
                                        style={{ width: '100%', maxHeight: '200px', borderRadius: '6px' }}
                                    >
                                        <source src={tutorDetail.video_introduction} type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                ) : (
                                    <p className="text-muted">Not uploaded</p>
                                )}
                            </div>

                            {/* Emirates ID Front */}
                            <div className="col-md-6">
                                <label className="text-muted small mb-2 d-block">Emirates ID - Front</label>
                                {tutorDetail.emirates_id_front ? (
                                    <div 
                                        className="border rounded p-2 text-center"
                                        style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                                        onClick={() => setSelectedImage(tutorDetail.emirates_id_front)}
                                    >
                                        <img
                                            src={tutorDetail.emirates_id_front}
                                            alt="Emirates ID Front"
                                            style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px' }}
                                        />
                                        <p className="small text-muted mt-2 mb-0">Click to enlarge</p>
                                    </div>
                                ) : (
                                    <p className="text-muted">Not uploaded</p>
                                )}
                            </div>

                            {/* Emirates ID Back */}
                            <div className="col-md-6">
                                <label className="text-muted small mb-2 d-block">Emirates ID - Back</label>
                                {tutorDetail.emirates_id_back ? (
                                    <div 
                                        className="border rounded p-2 text-center"
                                        style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                                        onClick={() => setSelectedImage(tutorDetail.emirates_id_back)}
                                    >
                                        <img
                                            src={tutorDetail.emirates_id_back}
                                            alt="Emirates ID Back"
                                            style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px' }}
                                        />
                                        <p className="small text-muted mt-2 mb-0">Click to enlarge</p>
                                    </div>
                                ) : (
                                    <p className="text-muted">Not uploaded</p>
                                )}
                            </div>

                            {/* Tutor Permit Front */}
                            <div className="col-md-6">
                                <label className="text-muted small mb-2 d-block">Tutor Permit - Front</label>
                                {tutorDetail.tutor_permit_front ? (
                                    <div 
                                        className="border rounded p-2 text-center"
                                        style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                                        onClick={() => setSelectedImage(tutorDetail.tutor_permit_front)}
                                    >
                                        <img
                                            src={tutorDetail.tutor_permit_front}
                                            alt="Tutor Permit Front"
                                            style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px' }}
                                        />
                                        <p className="small text-muted mt-2 mb-0">Click to enlarge</p>
                                    </div>
                                ) : (
                                    <p className="text-muted">Not uploaded</p>
                                )}
                            </div>

                            {/* Tutor Permit Back */}
                            <div className="col-md-6">
                                <label className="text-muted small mb-2 d-block">Tutor Permit - Back</label>
                                {tutorDetail.tutor_permit_back ? (
                                    <div 
                                        className="border rounded p-2 text-center"
                                        style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                                        onClick={() => setSelectedImage(tutorDetail.tutor_permit_back)}
                                    >
                                        <img
                                            src={tutorDetail.tutor_permit_back}
                                            alt="Tutor Permit Back"
                                            style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px' }}
                                        />
                                        <p className="small text-muted mt-2 mb-0">Click to enlarge</p>
                                    </div>
                                ) : (
                                    <p className="text-muted">Not uploaded</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Modal */}
            {selectedImage && (
                <ImageModal 
                    image={selectedImage} 
                    onClose={() => setSelectedImage(null)} 
                />
            )}
        </div>
    )
}

export default ViewTutorDetail